/**
 * The purchase a signed-out viewer had already assembled when the checkout
 * auth drawer opened.
 *
 * Booking is gated on identity, not on a profile (see
 * `plans/guest-checkout-auth.md`), so a guest can fill in a whole booking
 * before we ever ask who they are. Without this record, signing in throws that
 * work away: web OAuth is a full document navigation, so every piece of React
 * state is gone by the time the viewer lands back on the page.
 *
 * `sessionStorage` is the right home for it. It survives the cross-origin
 * round-trip through Google/Apple within the same tab, it dies with the tab
 * (so an abandoned intent cannot resurface days later on a shared device), and
 * the native path — which stays in one JS runtime and merely `push`es the gate
 * URL — reads it back just the same.
 *
 * One slot, consumed on read: a resumed submit creates a real reservation, so
 * the record has to be gone *before* the action fires rather than after it
 * succeeds. A crash mid-submit then costs the viewer a re-tap, which is the
 * cheap failure. The other ordering costs them a duplicate booking.
 */

const STORAGE_KEY = "hiive:checkout-intent";

/** Long enough to sign in and pick an account; short enough that prices, seats
 * and booking windows have not moved underneath the selection. */
const MAX_AGE_MS = 15 * 60 * 1000;

export type CheckoutIntentKind =
  | "event-booking"
  | "venue-booking"
  /**
   * The tier a signed-out viewer tapped on the tiers page. Not a purchase in
   * the free case, but the same problem: OAuth is a document navigation, and
   * without this they come back to a grid of five tiers with no memory of which
   * one they had chosen — on the very screen where picking the wrong one is the
   * failure we are trying to prevent.
   */
  | "tier-join";

export type CheckoutIntent<TPayload = unknown> = {
  kind: CheckoutIntentKind;
  /** Identifies the exact thing being bought, e.g. `orca/dive-retreat`. */
  key: string;
  payload: TPayload;
  savedAt: number;
};

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    // Safari in Lockdown Mode / private-mode quota failures. Losing the resume
    // is survivable; throwing out of a click handler is not.
    return null;
  }
}

export function stashCheckoutIntent<TPayload>(
  intent: Omit<CheckoutIntent<TPayload>, "savedAt">,
) {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...intent, savedAt: Date.now() }),
    );
  } catch {
    // Same reasoning as above.
  }
}

export function clearCheckoutIntent() {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to recover from: a stale record still expires on age.
  }
}

/**
 * Read and consume the stashed intent, but only when it is the one this caller
 * is waiting for. Anything else — a different event, a different kind, a
 * record older than {@link MAX_AGE_MS}, malformed JSON — returns `null`, and
 * only the caller's own match is cleared, so an unrelated page never eats an
 * intent meant for somewhere else.
 */
export function consumeCheckoutIntent<TPayload>(match: {
  kind: CheckoutIntentKind;
  key: string;
}): TPayload | null {
  const store = storage();
  if (!store) return null;

  let raw: string | null = null;
  try {
    raw = store.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: CheckoutIntent<TPayload> | null = null;
  try {
    parsed = JSON.parse(raw) as CheckoutIntent<TPayload>;
  } catch {
    clearCheckoutIntent();
    return null;
  }

  if (
    !parsed ||
    parsed.kind !== match.kind ||
    parsed.key !== match.key ||
    typeof parsed.savedAt !== "number" ||
    !Object.hasOwn(parsed, "payload")
  ) {
    return null;
  }

  clearCheckoutIntent();

  if (Date.now() - parsed.savedAt > MAX_AGE_MS) return null;

  return parsed.payload;
}
